// features/automationScripts/utils/assertionDisplay.js
//
// Assertions are the point of a run, but they arrive as one sentence per check:
//
//   "statusCode should have been \"200\" but was \"500\""
//
// which asks the reader to find the field, the comparison, the expected value
// and the actual value inside a line of prose. This module turns whatever the
// stream sent into the pieces a view can lay out separately — field,
// comparison, expected, actual, status — and phrases them the way a person
// would say them.
//
// Three input shapes are handled, because all three exist:
//
//   - a structured result ({ field, comparison, expected, actual, passed }),
//     which is where the executor is heading and is always preferred;
//   - the executor's current sentences, in the grammar below;
//   - the sentences it used to send, which are still sitting in every summary
//     already written to S3, so a results page has to keep reading them.
//
// Parsing prose is a poor way to get at data and the executor team says so
// themselves — the sentence is meant for display. Until the parts arrive on the
// payload this is the only way to lay them out, so the parser is written to
// degrade rather than fail: an unrecognised comparison is carried through as
// its own wording instead of collapsing the row back to raw text.

import { ASSERTION_OPERATORS } from '../config/automationConstants';
import { humaniseIdentifier } from './labels';
import { describeRow, parseRow } from './dbAssertionKeys';

/** How each operator reads in a sentence. Keyed by the editor's operator values. */
export const COMPARISON_PHRASES = {
  EQUALS: 'is equal to',
  NOT_EQUALS: 'is not equal to',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'does not contain',
  GREATER_THAN: 'is greater than',
  LESS_THAN: 'is less than',
  NULL: 'is null',
  NOT_NULL: 'is not null',
  TRUE: 'is true',
  FALSE: 'is false',
  MATCHES: 'matches',
  STARTS_WITH: 'starts with',
  ENDS_WITH: 'ends with',
};

/**
 * Operators that carry no expected value of their own — the operator *is* the
 * expectation. Mirrors NO_VALUE_OPS in the assertions editor.
 */
export const SELF_EVIDENT_COMPARISONS = new Set(['NULL', 'NOT_NULL', 'TRUE', 'FALSE']);

/** Every operator the editor can produce, so an unknown one is recognisable as such. */
const KNOWN_OPERATORS = new Set(ASSERTION_OPERATORS.map((o) => o.value));

/**
 * The field as a person would name it:
 *   "response.success"        → "Success"
 *   "response.statusCode"     → "Status code"
 *   "response.data.order_id"  → "Order id"
 *   "response.URL"            → "URL"
 *
 * The scope prefix is dropped — the row already says which side it came from.
 */
export const humaniseField = (path) => {
  if (path == null || path === '') return '';
  const last = String(path).split('.').filter(Boolean).pop() || String(path);
  return humaniseIdentifier(last);
};

/* ── The executor's grammar ──────────────────────────────────────────
 *
 *   PASS   <field> was <value>, as expected
 *   PASS   <field> was <value>, which <relation> <value>, as expected
 *   FAIL   <field> should [not] have <requirement> <value> but was <value>
 *   FAIL   <field> should [not] have <requirement> <value> but it was
 *
 * <value> is a quoted string or the bare word `empty`.
 */

/** The requirement verb in a FAIL sentence, mapped to the editor's operator. */
const REQUIREMENT_OPERATORS = {
  been: 'EQUALS',
  'been greater than': 'GREATER_THAN',
  'been less than': 'LESS_THAN',
  contained: 'CONTAINS',
  matched: 'MATCHES',
  'started with': 'STARTS_WITH',
  'ended with': 'ENDS_WITH',
  'been empty': 'NULL',
  'been true': 'TRUE',
  'been false': 'FALSE',
};

/** What "should not have …" means, for the requirements that can be negated. */
const NEGATED_OPERATORS = {
  EQUALS: 'NOT_EQUALS',
  CONTAINS: 'NOT_CONTAINS',
  NULL: 'NOT_NULL',
  TRUE: 'FALSE',
  FALSE: 'TRUE',
};

/** The pass sentences state the comparison in the operator's own words. */
const OPERATORS_BY_PHRASE = Object.entries(COMPARISON_PHRASES)
  .reduce((acc, [operator, phrase]) => ({ ...acc, [phrase]: operator }), {});

const EMPTY_WORD = 'empty';

/** A value as written: `"200"` → `200`, and the bare word `empty` left as is. */
const readValue = (raw) => {
  if (raw == null) return null;
  const text = String(raw).trim();
  const quoted = text.match(/^"([\s\S]*)"$/);
  if (quoted) return quoted[1];
  return text === EMPTY_WORD ? EMPTY_WORD : text;
};

/**
 * Split a requirement into its verb and the value it names.
 * `been greater than "3"` → { phrase: 'been greater than', value: '3' }
 * `been empty`            → { phrase: 'been empty', value: null }
 */
const splitRequirement = (raw) => {
  const text = String(raw ?? '').trim();
  const withValue = text.match(/^([\s\S]*?)\s*("[\s\S]*")$/);
  if (withValue) {
    return { phrase: withValue[1].trim(), value: readValue(withValue[2]) };
  }
  return { phrase: text, value: null };
};

/**
 * The operator a requirement phrase means, negation applied. Returns null when
 * the phrase is not one we know, so the caller can fall back to showing the
 * executor's own wording rather than mislabelling the check.
 */
const operatorForRequirement = (phrase, negated) => {
  let base = REQUIREMENT_OPERATORS[phrase.toLowerCase()] ?? null;
  if (!base) {
    // A row-scoped sentence names the operator outright — "matched EQUALS" —
    // so when the last word is an operator we know, that is the comparison.
    const lastWord = phrase.trim().split(/\s+/).pop() ?? '';
    if (KNOWN_OPERATORS.has(lastWord.toUpperCase())) base = lastWord.toUpperCase();
  }
  if (!base) return null;
  return negated ? NEGATED_OPERATORS[base] ?? base : base;
};

/**
 * The row selector a scope clause names: "all 500 rows" → all, "row 2" → 2,
 * "the first row" → first. Null when the clause is not about rows at all, so a
 * sentence that merely contains the word "in" is not mistaken for a scoped one.
 */
const readScope = (clause) => {
  const text = String(clause || '').trim().toLowerCase();
  if (!/\brows?\b/.test(text)) return null;
  const positional = text.match(/\brow\s+(\d+)\b/);
  if (positional) return positional[1];
  if (/\bno\b|\bnone\b/.test(text)) return 'none';
  if (/\ball\b|\bevery\b/.test(text)) return 'all';
  if (/\bany\b|\bat least one\b/.test(text)) return 'any';
  if (/\bfirst\b/.test(text)) return 'first';
  if (/\blast\b/.test(text)) return 'last';
  return null;
};

const FAIL_RE = /^([\s\S]+?) should (not )?have ([\s\S]+?) but (?:was ([\s\S]+)|it was)$/;
/*
 * A database assertion carrying a row selector says which rows it covered, and
 * reports a count instead of one value when it covered more than one:
 *
 *   currency should have matched EQUALS "ZAR" in all 500 rows, but only 498 did
 *
 * FAIL_RE cannot read that — the tail is a tally, not a value — so this pulls
 * out the scope clause and leaves the tally where the actual value goes, which
 * is what it is standing in for. Tried only after the strict patterns, so no
 * sentence that already parsed changes.
 */
const FAIL_SCOPED_RE = /^([\s\S]+?) should (not )?have ([\s\S]+?) in ([^,]+?)\s*,?\s+but ([\s\S]+)$/;
const PASS_RELATION_RE = /^([\s\S]+?) was ([\s\S]+?), which ([\s\S]+?), as expected$/;
const PASS_RE = /^([\s\S]+?) was ([\s\S]+?), as expected$/;
const LEGACY_RE = /^Response value\s+([^:]+):\s*([\s\S]*)$/;

/** Phrases longest-first, so "is not equal to" is found before "is equal to". */
const PHRASES_BY_LENGTH = Object.entries(COMPARISON_PHRASES)
  .sort((a, b) => b[1].length - a[1].length);

/**
 * The sentences the executor used to send, still present in every summary
 * written before the wording changed:
 *
 *   "Response value response.success: true is equal to expected value: true"
 *   "Response value response.otpReference: abc is not null"
 */
const parseLegacySentence = (text) => {
  const head = text.match(LEGACY_RE);
  if (!head) return null;
  const field = head[1].trim();
  const tail = head[2];

  for (const [comparison, phrase] of PHRASES_BY_LENGTH) {
    const marker = ` ${phrase} expected value:`;
    const at = tail.indexOf(marker);
    if (at !== -1) {
      return {
        field,
        comparison,
        actual: tail.slice(0, at).trim(),
        expected: tail.slice(at + marker.length).trim(),
        passed: null,
      };
    }
  }

  for (const [comparison, phrase] of PHRASES_BY_LENGTH) {
    const suffix = ` ${phrase}`;
    if (tail.endsWith(suffix)) {
      return {
        field,
        comparison,
        actual: tail.slice(0, tail.length - suffix.length).trim(),
        expected: null,
        passed: null,
      };
    }
  }

  return null;
};

/**
 * Pull a sentence apart into { field, comparison, expected, actual, passed }.
 *
 * `passed` comes from the sentence's own shape, which is better than the
 * block's aggregate status — one failed check among three used to paint all
 * three as failed. It is null when the shape does not say.
 *
 * `comparison` may be the executor's own wording rather than one of the
 * editor's operators, when the requirement is one we do not have a name for.
 *
 * Returns null when nothing matches, so the caller shows the text as it
 * arrived instead of inventing parts for it.
 */
export const parseAssertionSentence = (text) => {
  if (typeof text !== 'string' || text.trim() === '') return null;
  const trimmed = text.trim();

  const scoped = trimmed.match(FAIL_SCOPED_RE);
  const scopedRow = scoped ? readScope(scoped[4]) : null;
  if (scoped && scopedRow) {
    const [, field, not, requirement, , tally] = scoped;
    const { phrase, value } = splitRequirement(requirement);
    const negated = Boolean(not);
    return {
      field: field.trim(),
      comparison: operatorForRequirement(phrase, negated) ?? `should ${negated ? 'not ' : ''}have ${phrase}`,
      expected: value,
      actual: readValue(tally),
      row: scopedRow,
      passed: false,
    };
  }

  const failed = trimmed.match(FAIL_RE);
  if (failed) {
    const [, field, not, requirement, actual] = failed;
    const { phrase, value } = splitRequirement(requirement);
    const negated = Boolean(not);
    return {
      field: field.trim(),
      // An unknown verb is carried through as written — "should have been
      // rounded to" still reads correctly beside the two values.
      comparison: operatorForRequirement(phrase, negated) ?? `should ${negated ? 'not ' : ''}have ${phrase}`,
      expected: value,
      // "but it was" is the executor saying the value was the thing it should
      // not have been — empty.
      actual: actual == null ? EMPTY_WORD : readValue(actual),
      passed: false,
    };
  }

  const passedWithRelation = trimmed.match(PASS_RELATION_RE);
  if (passedWithRelation) {
    const [, field, actual, relation] = passedWithRelation;
    const { phrase, value } = splitRequirement(relation);
    return {
      field: field.trim(),
      comparison: OPERATORS_BY_PHRASE[phrase.toLowerCase()] ?? phrase,
      expected: value,
      actual: readValue(actual),
      passed: true,
    };
  }

  const passed = trimmed.match(PASS_RE);
  if (passed) {
    const [, field, actual] = passed;
    const value = readValue(actual);
    return {
      field: field.trim(),
      // Nothing else was stated, so the check was an equality against the
      // value it reports.
      comparison: 'EQUALS',
      expected: value,
      actual: value,
      passed: true,
    };
  }

  return parseLegacySentence(trimmed);
};

const firstDefined = (...values) => values.find((v) => v !== undefined && v !== null);

/**
 * One assertion in the shape a view needs, from whatever the stream sent.
 *
 * @param {Object|string} result          one entry of assertionResponse.results
 * @param {boolean} fallbackPassed        assertionResponse.passed, used only
 *                                        when neither the entry nor its
 *                                        sentence says how this check went
 */
export const toAssertionView = (result, fallbackPassed) => {
  const structured = typeof result === 'object' && result !== null;

  const message = structured
    ? firstDefined(result.message, result.description) ?? null
    : (typeof result === 'string' ? result : String(result));

  // A structured result may still only carry a sentence; parse it for the
  // pieces it did not spell out.
  const parsed = parseAssertionSentence(message);

  const field = structured
    ? firstDefined(result.field, result.path, result.key, parsed?.field) ?? null
    : parsed?.field ?? null;

  const rawComparison = structured
    ? firstDefined(result.comparison, result.operator, result.type, parsed?.comparison) ?? null
    : parsed?.comparison ?? null;

  // Operator values are upper case; the executor's own wording is not, and
  // upper-casing it would shout.
  const comparison = rawComparison == null
    ? null
    : (KNOWN_OPERATORS.has(String(rawComparison).toUpperCase())
      ? String(rawComparison).toUpperCase()
      : String(rawComparison));

  return {
    passed: structured
      ? firstDefined(result.pass, result.passed, parsed?.passed, fallbackPassed) ?? false
      : firstDefined(parsed?.passed, fallbackPassed) ?? false,
    field,
    comparison,
    isKnownComparison: comparison != null && KNOWN_OPERATORS.has(comparison),
    expected: structured
      ? firstDefined(result.expected, parsed?.expected) ?? null
      : parsed?.expected ?? null,
    actual: structured
      ? firstDefined(result.actual, parsed?.actual) ?? null
      : parsed?.actual ?? null,
    // Database assertions only: which rows the check covered. Empty for every
    // other engine, which has no rows to scope to.
    row: structured
      ? parseRow(firstDefined(result.row, result.rowSelector, parsed?.row) ?? '')
      : parseRow(parsed?.row ?? ''),
    text: message,
  };
};

/** True when there is enough to lay out instead of printing the sentence. */
export const isStructured = (view) => !!view.field && !!view.comparison;

/**
 * The friendly restatement, e.g. "Success is equal to true". `value` is the
 * side being described — pass the actual for the actual line, the expected for
 * the expected line. A self-evident comparison needs no value.
 */
export const describeAssertion = (view, value) => {
  const name = humaniseField(view.field);
  const phrase = COMPARISON_PHRASES[view.comparison] || view.comparison;
  if (!name) return null;
  if (!phrase) return name;
  if (SELF_EVIDENT_COMPARISONS.has(view.comparison)) return `${name} ${phrase}`;
  if (value == null || value === '') return `${name} ${phrase}`;
  return `${name} ${phrase} ${value}`;
};

/**
 * Which rows the check covered — "in any row", "in all rows", "in row 2" — or
 * an empty string when the assertion was not row-scoped.
 */
export const assertionScope = (view) => describeRow(view.row);

/**
 * The comparison with neither value in it — "Success is equal to" — so the
 * view can put the values beside it as their own labelled facts.
 */
export const assertionStem = (view) => describeAssertion(view, null);

/**
 * The stem in its two parts, so a view can weight them differently: the
 * response value being checked is the subject, the comparison is only the
 * connective between it and the values.
 *
 *   { subject: 'Success', phrase: 'is equal to' }
 */
export const assertionParts = (view) => ({
  subject: humaniseField(view.field),
  phrase: COMPARISON_PHRASES[view.comparison] || view.comparison || '',
});
